import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getOrInitializeAccount,
  refreshAccountData,
  AccountData,
  AccountType,
  updateAccountType,
  getInvitedFriends,
  refreshInvitedFriends,
  addInvitedFriend,
  removeInvitedFriend,
  getEffectiveResponseCount,
  type InvitedFriend,
} from '../utils/accountUtils';
import { listSavedJourneys, deleteSavedJourney } from '../services/savedJourneysFirestore';
import type { SavedJourney } from '../types';

type EditMode = 'none' | 'profile' | 'password';

export interface ProfileSectionProps {
  onLoadSavedJourney?: (journey: SavedJourney) => void;
  preferredLanguage?: string;
  languageOptions?: Array<{ label: string; value: string }>;
  onPreferredLanguageChange?: (lang: string) => void;
}

interface AccountFeature {
  label: string;
  good: boolean;
  color?: 'red' | 'yellow' | 'green'; // Optional color override
}

interface AccountOption {
  type: AccountType;
  label: string;
  price: string;
  priceMonthly?: string;
  priceYearly?: string;
  priceYearlyOriginal?: string;
  yearlySavings?: string;
  color: string;
  features: AccountFeature[];
}

const ACCOUNT_OPTIONS: AccountOption[] = [
  {
    type: 'Free',
    label: 'Free',
    price: '$0/month',
    priceMonthly: '$0/month',
    priceYearly: '$0/year',
    color: 'gray',
    features: [
      { label: 'Online data', good: true },
      { label: 'Limited agent call', good: false },
      { label: 'Ads', good: false },
      { label: 'Memoryless', good: false },
      { label: 'Basic features', good: false, color: 'red' },
      { label: 'English only', good: false },
    ],
  },
  {
    type: 'Professional',
    label: 'Standard',
    price: '$14.99/month',
    priceMonthly: '$14.99/month',
    priceYearly: '$129.99/year',
    priceYearlyOriginal: '$180/year',
    yearlySavings: 'Save 40%',
    color: 'blue',
    features: [
      { label: 'Online data', good: true },
      { label: 'Unlimited agent call', good: true },
      { label: 'Ad-free', good: true },
      { label: 'Memory', good: true },
      { label: 'Intermediate features', good: true, color: 'yellow' },
      { label: 'English only', good: false },
    ],
  },
  {
    type: 'Premium',
    label: 'Premium',
    price: '$19.99/month',
    priceMonthly: '$19.99/month',
    priceYearly: '$169.99/year',
    priceYearlyOriginal: '$240/year',
    yearlySavings: 'Save 40%',
    color: 'purple',
    features: [
      { label: 'Online data', good: true },
      { label: 'Unlimited agent call', good: true },
      { label: 'Ad-free', good: true },
      { label: 'Memory', good: true },
      { label: 'Advanced features', good: true, color: 'green' },
      { label: 'Multilingual', good: true },
    ],
  },
];

export const ProfileSection: React.FC<ProfileSectionProps> = ({
  onLoadSavedJourney,
  preferredLanguage,
  languageOptions,
  onPreferredLanguageChange,
}) => {
  const { user, signOut, updateUserProfile, updateUserPassword, error, clearError } = useAuth();
  const [editMode, setEditMode] = useState<EditMode>('none');
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [languageMessage, setLanguageMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accountData, setAccountData] = useState<AccountData | null>(null);
  const [showAccountOptionsPage, setShowAccountOptionsPage] = useState(false);
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState(false);
  const [pendingAccountType, setPendingAccountType] = useState<AccountType | null>(null);
  const [showInviteFriendsPage, setShowInviteFriendsPage] = useState(false);
  const [showSavedJourneysPage, setShowSavedJourneysPage] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [invitedFriendsList, setInvitedFriendsList] = useState<InvitedFriend[]>([]);
  const [savedJourneysList, setSavedJourneysList] = useState<SavedJourney[]>([]);
  const [savedJourneysError, setSavedJourneysError] = useState<string | null>(null);
  const [isSavedJourneysLoading, setIsSavedJourneysLoading] = useState(false);
  const [deletingJourneyId, setDeletingJourneyId] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  // Load account data when user changes
  useEffect(() => {
    if (user?.uid) {
      (async () => {
        const data = await getOrInitializeAccount(user.uid);
        setAccountData(data);
      })();
    } else {
      setAccountData(null);
    }
  }, [user?.uid]);

  // Refresh account data periodically to get updated response count
  useEffect(() => {
    if (!user?.uid) return;

    const interval = setInterval(() => {
      void (async () => {
        const data = await refreshAccountData(user.uid!);
        setAccountData(data);
      })();
    }, 3000);

    return () => clearInterval(interval);
  }, [user?.uid]);

  // Load invited friends when opening Invite friends page
  useEffect(() => {
    if (user?.uid && showInviteFriendsPage) {
      void (async () => {
        const list = await refreshInvitedFriends(user.uid);
        setInvitedFriendsList(list);
      })();
    }
  }, [user?.uid, showInviteFriendsPage]);

  // Load saved journeys when opening Saved Journeys page (from Firestore)
  useEffect(() => {
    if (!user?.uid || !showSavedJourneysPage) return;
    let isCancelled = false;
    const loadSavedJourneys = async () => {
      setIsSavedJourneysLoading(true);
      setSavedJourneysError(null);
      try {
        const list = await listSavedJourneys(user.uid);
        if (!isCancelled) {
          setSavedJourneysList(list);
        }
      } catch (err) {
        if (!isCancelled) {
          setSavedJourneysError('Failed to load saved journeys. Please try again.');
        }
      } finally {
        if (!isCancelled) {
          setIsSavedJourneysLoading(false);
        }
      }
    };

    void loadSavedJourneys();
    return () => {
      isCancelled = true;
    };
  }, [user?.uid, showSavedJourneysPage]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      // Error handled by context
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMessage(null);
    clearError();

    if (!displayName.trim()) {
      setLocalError('Display name cannot be empty');
      return;
    }

    setIsSubmitting(true);
    try {
      await updateUserProfile(displayName);
      setSuccessMessage('Profile updated successfully!');
      setEditMode('none');
    } catch (err) {
      // Error handled by context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setSuccessMessage(null);
    clearError();

    if (newPassword !== confirmPassword) {
      setLocalError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setLocalError('Password must be at least 6 characters');
      return;
    }

    setIsSubmitting(true);
    try {
      await updateUserPassword(currentPassword, newPassword);
      setSuccessMessage('Password updated successfully!');
      setEditMode('none');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      // Error handled by context
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelEdit = () => {
    setEditMode('none');
    setLocalError(null);
    setSuccessMessage(null);
    clearError();
    setDisplayName(user?.displayName || '');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const displayError = localError || error;
  const isPremiumUser = accountData?.accountType === 'Premium';

  const handlePreferredLanguageChange = (nextLanguage: string) => {
    setLocalError(null);
    setLanguageMessage(null);
    setSuccessMessage(null);
    if (!isPremiumUser) {
      onPreferredLanguageChange?.('en-US');
      setLanguageMessage(
        'Language customization is available on Premium plans. Upgrade to Premium to choose a non-English chat language.'
      );
      return;
    }
    onPreferredLanguageChange?.(nextLanguage);
  };

  // Check if user signed in with Google (password change not available)
  const isGoogleUser = user?.providerData[0]?.providerId === 'google.com';

  // Get user initials for avatar fallback
  const getInitials = () => {
    if (user?.displayName) {
      return user.displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.email?.[0].toUpperCase() || '?';
  };

  // Account options page (Upgrade / Modify)
  if (showAccountOptionsPage && user?.uid) {
    const isFree = accountData?.accountType === 'Free';
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={() => setShowAccountOptionsPage(false)}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
        >
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          <span className="text-sm font-medium">Back</span>
        </button>
        <h2 className="text-xl font-semibold text-gray-900">
          {isFree ? 'Upgrade your plan' : 'Modify your plan'}
        </h2>
        <p className="text-sm text-gray-600">
          {isFree ? 'Choose a plan to get more out of RouteX.' : 'Change your account type.'}
        </p>
        <p className="text-sm text-gray-600">
          If you are a business or corporate and aim to use RouteX AI, contact us to get a quote.
        </p>

        {/* Billing cycle toggle: Monthly / Yearly */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
          <span className="text-sm font-medium text-gray-700">Billing:</span>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-white">
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                billingCycle === 'monthly' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                billingCycle === 'yearly' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Yearly
            </button>
          </div>
          {billingCycle === 'yearly' && (
            <span className="text-xs font-medium text-green-600">Save 40%</span>
          )}
        </div>

        <div className="space-y-4">
          {ACCOUNT_OPTIONS.map((opt) => {
            const displayPrice =
              billingCycle === 'yearly' && opt.priceYearly
                ? opt.priceYearly
                : (opt.priceMonthly ?? opt.price);
            const showYearlyBadge = billingCycle === 'yearly' && opt.yearlySavings;
            return (
            <button
              key={opt.type}
              type="button"
              onClick={() => {
                const isDowngradeToFree =
                  opt.type === 'Free' &&
                  (accountData?.accountType === 'Professional' || accountData?.accountType === 'Premium');
                if (isDowngradeToFree) {
                  setPendingAccountType('Free');
                  setShowDowngradeConfirm(true);
                  return;
                }
                void (async () => {
                  await updateAccountType(user.uid, opt.type);
                  const next = await refreshAccountData(user.uid);
                  setAccountData(next);
                  setShowAccountOptionsPage(false);
                  setSuccessMessage(`Account set to ${opt.label}.`);
                  setTimeout(() => setSuccessMessage(null), 3000);
                })();
              }}
              className={`w-full text-left rounded-xl border-2 transition-all overflow-hidden ${
                accountData?.accountType === opt.type
                  ? opt.color === 'gray'
                    ? 'border-gray-400 bg-gray-50'
                    : opt.color === 'blue'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between p-4 pb-2">
                <div>
                  <p className="font-semibold text-gray-900">{opt.label}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {billingCycle === 'yearly' && opt.priceYearlyOriginal && (
                      <span className="text-sm font-medium text-gray-400 line-through">
                        {opt.priceYearlyOriginal}
                      </span>
                    )}
                    <p className="text-sm font-medium text-gray-600">{displayPrice}</p>
                    {showYearlyBadge && (
                      <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-0.5 rounded">
                        {opt.yearlySavings}
                      </span>
                    )}
                  </div>
                </div>
                {accountData?.accountType === opt.type && (
                  <span className="material-symbols-outlined text-green-600">check_circle</span>
                )}
              </div>
              <div className="px-4 pb-4 flex flex-wrap gap-2">
                {opt.features.map((f) => {
                  // Determine color: use explicit color if provided, otherwise use good/bad logic
                  let colorClass = '';
                  if (f.color === 'yellow') {
                    colorClass = 'bg-yellow-100 text-yellow-800';
                  } else if (f.color === 'green' || (!f.color && f.good)) {
                    colorClass = 'bg-green-100 text-green-800';
                  } else {
                    colorClass = 'bg-red-100 text-red-800';
                  }

                  return (
                    <span
                      key={f.label}
                      className={`inline-block px-2.5 py-1 rounded-lg text-xs font-medium ${colorClass}`}
                    >
                      {f.label}
                    </span>
                  );
                })}
              </div>
            </button>
          );
          })}
        </div>

        {/* Downgrade to Free confirmation modal */}
        {showDowngradeConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="material-symbols-outlined text-amber-500 text-3xl">warning</span>
                <h3 className="text-lg font-semibold text-gray-900">Are you sure?</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Switching to Free will cause you to <strong>lose your data</strong>. The agent
                remaining response limit will be <strong>applied immediately</strong>.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDowngradeConfirm(false);
                    setPendingAccountType(null);
                  }}
                  className="flex-1 py-2.5 px-4 rounded-xl border-2 border-gray-200 text-gray-700 font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (pendingAccountType && user?.uid) {
                      void (async () => {
                        await updateAccountType(user.uid, pendingAccountType);
                        const next = await refreshAccountData(user.uid);
                        setAccountData(next);
                        setShowAccountOptionsPage(false);
                        setShowDowngradeConfirm(false);
                        setPendingAccountType(null);
                        setSuccessMessage('Account set to Free.');
                        setTimeout(() => setSuccessMessage(null), 3000);
                      })();
                    }
                  }}
                  className="flex-1 py-2.5 px-4 rounded-xl bg-amber-500 text-white font-medium hover:bg-amber-600"
                >
                  Yes, switch to Free
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Invite friends tab
  if (showInviteFriendsPage && user?.uid) {
    const handleAddInvite = (e: React.FormEvent) => {
      e.preventDefault();
      setInviteError(null);
      void (async () => {
        const result = await addInvitedFriend(user.uid, inviteEmail);
        if (result.ok) {
          setInviteEmail('');
          setInvitedFriendsList(getInvitedFriends(user.uid));
        } else {
          setInviteError(result.error || 'Could not add');
        }
      })();
    };
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={() => setShowInviteFriendsPage(false)}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
        >
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          <span className="text-sm font-medium">Back</span>
        </button>
        <h2 className="text-xl font-semibold text-gray-900">Invite friends</h2>
        <p className="text-sm text-gray-600">
          Up to 10 friends, +2 agent calls when they become standard/premium user.
        </p>

        {/* Add friend (if under 10) */}
        {invitedFriendsList.length < 10 && (
          <form onSubmit={handleAddInvite} className="flex gap-2">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Friend's email"
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
            />
            <button
              type="submit"
              className="px-4 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700"
            >
              Add
            </button>
          </form>
        )}
        {inviteError && (
          <p className="text-sm text-red-600">{inviteError}</p>
        )}

        {/* List of invited friends */}
        <div className="space-y-2">
          {invitedFriendsList.length === 0 ? (
            <p className="text-sm text-gray-500">No friends invited yet.</p>
          ) : (
            invitedFriendsList.map((friend) => (
              <div
                key={friend.email}
                className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-xl"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`material-symbols-outlined flex-shrink-0 ${friend.registered ? 'text-green-600' : 'text-gray-600'}`}>
                    {friend.registered ? 'check_circle' : 'mail'}
                  </span>
                  <span className={`text-sm truncate ${friend.registered ? 'text-green-700 font-medium' : 'text-gray-700'}`}>
                    {friend.email}
                  </span>
                  {friend.registered && (
                    <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                      <span className="material-symbols-outlined text-[14px]">check</span>
                      Registered
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    void (async () => {
                      await removeInvitedFriend(user.uid, friend.email);
                      setInvitedFriendsList(getInvitedFriends(user.uid));
                    })();
                  }}
                  className="text-sm text-red-600 hover:text-red-700 flex-shrink-0 ml-2"
                >
                  Remove
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // Saved Journeys tab
  if (showSavedJourneysPage && user?.uid) {
    const isFreeUser = accountData?.accountType === 'Free';
    if (isFreeUser) {
      return (
        <div className="space-y-6">
          <button
            type="button"
            onClick={() => setShowSavedJourneysPage(false)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
            <span className="text-sm font-medium">Back</span>
          </button>
          <h2 className="text-xl font-semibold text-gray-900">Saved Journeys</h2>
          <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-6 text-center">
            <span className="material-symbols-outlined text-4xl text-amber-500 mb-3 block">lock</span>
            <p className="text-gray-800 font-medium mb-1">Upgrade to view your saved journeys</p>
            <p className="text-sm text-gray-600 mb-4">
              Only Standard and Premium users can view and manage their saved journeys. Upgrade to access your list.
            </p>
            <button
              type="button"
              onClick={() => {
                setShowSavedJourneysPage(false);
                setShowAccountOptionsPage(true);
              }}
              className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700"
            >
              Upgrade
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={() => setShowSavedJourneysPage(false)}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
        >
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          <span className="text-sm font-medium">Back</span>
        </button>
        <h2 className="text-xl font-semibold text-gray-900">Saved Journeys</h2>
        <p className="text-sm text-gray-600">
          Journeys you saved from the map (bookmark). Tap Load to restore a journey and open the route.
        </p>
        {savedJourneysError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {savedJourneysError}
          </div>
        )}
        {isSavedJourneysLoading ? (
          <p className="text-sm text-gray-500 py-4">Loading saved journeys...</p>
        ) : savedJourneysList.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">No saved journeys yet. Plan a route on the map and save it with the bookmark button next to Go.</p>
        ) : (
          <div className="space-y-3">
            {savedJourneysList.map((journey) => (
              <div
                key={journey.id}
                className="p-4 bg-gray-50 rounded-xl border border-gray-100"
              >
                <p className="font-medium text-gray-900 truncate" title={journey.name}>
                  {journey.name}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {new Date(journey.savedAt).toLocaleDateString()} · {journey.places.length} stop{journey.places.length !== 1 ? 's' : ''}
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => {
                      onLoadSavedJourney?.(journey);
                      setShowSavedJourneysPage(false);
                    }}
                    className="flex-1 py-2 px-3 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
                  >
                    Load
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const deleteAndRefresh = async () => {
                        setSavedJourneysError(null);
                        setDeletingJourneyId(journey.id);
                        try {
                          await deleteSavedJourney(user.uid, journey.id);
                          const list = await listSavedJourneys(user.uid);
                          setSavedJourneysList(list);
                        } catch (err) {
                          setSavedJourneysError('Failed to delete saved journey. Please try again.');
                        } finally {
                          setDeletingJourneyId(null);
                        }
                      };
                      void deleteAndRefresh();
                    }}
                    disabled={deletingJourneyId === journey.id}
                    className="py-2 px-3 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-100"
                  >
                    {deletingJourneyId === journey.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-4">
          {/* Avatar */}
          {user?.photoURL ? (
            <img
              src={user.photoURL}
              alt={user.displayName || 'Profile'}
              className="w-16 h-16 rounded-full border-2 border-white/30"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold">
              {getInitials()}
            </div>
          )}

          {/* User info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg truncate">
              {user?.displayName || 'RouteX User'}
            </h3>
            <p className="text-white/80 text-sm truncate">{user?.email}</p>
            {isGoogleUser && (
              <div className="flex items-center gap-1 mt-1">
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                </svg>
                <span className="text-xs text-white/70">Google Account</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Account Type and Response Counter */}
      {accountData && (
        <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-4">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Account Information</h3>

          {/* Account Type */}
          <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-blue-600">account_circle</span>
              <div className="text-left">
                <p className="font-medium text-gray-900">Account Type</p>
                <p className="text-sm text-gray-500">
                  {accountData.accountType === 'Free' && 'Free'}
                  {accountData.accountType === 'Professional' && 'Standard'}
                  {accountData.accountType === 'Premium' && 'Premium'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                accountData.accountType === 'Free' ? 'bg-gray-100 text-gray-700' :
                accountData.accountType === 'Professional' ? 'bg-blue-100 text-blue-700' :
                'bg-purple-100 text-purple-700'
              }`}>
                {accountData.accountType === 'Professional' ? 'Standard' : accountData.accountType}
              </span>
              <button
                type="button"
                onClick={() => setShowAccountOptionsPage(true)}
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                {accountData.accountType === 'Free' ? 'Upgrade' : 'Modify'}
              </button>
            </div>
          </div>

          {/* Response Counter */}
          <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-blue-600">chat_bubble</span>
              <div className="text-left">
                <p className="font-medium text-gray-900">Responses Remaining</p>
                <p className="text-sm text-gray-500">
                  {accountData.accountType === 'Professional' || accountData.accountType === 'Premium'
                    ? 'Unlimited responses'
                    : (user?.uid ? getEffectiveResponseCount(user.uid) : accountData.responseCount) > 0
                      ? `${user?.uid ? getEffectiveResponseCount(user.uid) : accountData.responseCount} responses left`
                      : 'No responses remaining'}
                </p>
              </div>
            </div>
            <div className={`text-2xl font-bold ${
              accountData.accountType === 'Professional' || accountData.accountType === 'Premium'
                ? 'text-green-600'
                : (user?.uid ? getEffectiveResponseCount(user.uid) : accountData.responseCount) > 5
                  ? 'text-green-600'
                  : (user?.uid ? getEffectiveResponseCount(user.uid) : accountData.responseCount) > 0
                    ? 'text-yellow-600'
                    : 'text-red-600'
            }`}>
              {accountData.accountType === 'Professional' || accountData.accountType === 'Premium'
                ? '∞'
                : user?.uid ? getEffectiveResponseCount(user.uid) : accountData.responseCount}
            </div>
          </div>

          {/* Invite friends */}
          <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-blue-600">group_add</span>
              <div className="text-left">
                <p className="font-medium text-gray-900">Invite friends</p>
                <p className="text-sm text-gray-500">Up to 10 friends, +2 agent calls when they become standard/premium user.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowInviteFriendsPage(true)}
              className="text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              Invite
            </button>
          </div>

          {/* Saved Journeys */}
          <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-blue-600">bookmark</span>
              <div className="text-left">
                <p className="font-medium text-gray-900">Saved Journeys</p>
                <p className="text-sm text-gray-500">Load or delete journeys you saved from the map</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowSavedJourneysPage(true)}
              className="text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              Open
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      {displayError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <span className="material-symbols-outlined text-[20px]">error</span>
          <span className="text-sm">{displayError}</span>
        </div>
      )}

      {successMessage && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <span className="material-symbols-outlined text-[20px]">check_circle</span>
          <span className="text-sm">{successMessage}</span>
        </div>
      )}

      {/* Edit Profile Form */}
      {editMode === 'profile' && (
        <form onSubmit={handleUpdateProfile} className="bg-gray-50 rounded-xl p-4 space-y-4">
          <h4 className="font-medium text-gray-900">Edit Profile</h4>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full bg-white rounded-lg py-2 px-3 text-gray-900 border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={cancelEdit}
              className="flex-1 py-2 px-4 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-2 px-4 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                'Save'
              )}
            </button>
          </div>
        </form>
      )}

      {/* Change Password Form */}
      {editMode === 'password' && (
        <form onSubmit={handleUpdatePassword} className="bg-gray-50 rounded-xl p-4 space-y-4">
          <h4 className="font-medium text-gray-900">Change Password</h4>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="w-full bg-white rounded-lg py-2 px-3 text-gray-900 border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              className="w-full bg-white rounded-lg py-2 px-3 text-gray-900 border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="w-full bg-white rounded-lg py-2 px-3 text-gray-900 border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={cancelEdit}
              className="flex-1 py-2 px-4 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-2 px-4 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                'Update'
              )}
            </button>
          </div>
        </form>
      )}

      {/* Account Actions */}
      {editMode === 'none' && (
        <div className="space-y-2">
          {/* Edit Profile */}
          <button
            onClick={() => {
              setEditMode('profile');
              setLocalError(null);
              setSuccessMessage(null);
            }}
            className="w-full flex items-center justify-between py-3 px-4 bg-white rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-gray-500">edit</span>
              <span className="text-gray-900">Edit Profile</span>
            </div>
            <span className="material-symbols-outlined text-gray-400">chevron_right</span>
          </button>

          {/* Preferred Language */}
          {languageOptions && languageOptions.length > 0 && (
            <div className="w-full py-3 px-4 bg-white rounded-xl border border-gray-100">
              <div className="flex items-start gap-3 mb-3">
                <span className="material-symbols-outlined text-gray-500">language</span>
                <div>
                  <p className="text-gray-900">Preferred Language</p>
                  <p className="text-xs text-gray-500">
                    Used for voice input and translated chat responses.
                  </p>
                </div>
              </div>
              <select
                value={isPremiumUser ? (preferredLanguage || 'en-US') : 'en-US'}
                onChange={(e) => handlePreferredLanguageChange(e.target.value)}
                className="w-full rounded-xl border-2 border-gray-200 bg-white px-4 py-3 text-gray-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all font-medium"
                aria-label="Preferred language"
              >
                {languageOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} ({opt.value})
                  </option>
                ))}
              </select>
              {languageMessage && (
                <p className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {languageMessage}
                </p>
              )}
            </div>
          )}

          {/* Change Password (only for email users) */}
          {!isGoogleUser && (
            <button
              onClick={() => {
                setEditMode('password');
                setLocalError(null);
                setSuccessMessage(null);
              }}
              className="w-full flex items-center justify-between py-3 px-4 bg-white rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-gray-500">lock</span>
                <span className="text-gray-900">Change Password</span>
              </div>
              <span className="material-symbols-outlined text-gray-400">chevron_right</span>
            </button>
          )}

          {/* Sign Out */}
          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-between py-3 px-4 bg-white rounded-xl border border-gray-100 hover:bg-red-50 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-red-500">logout</span>
              <span className="text-red-600">Sign Out</span>
            </div>
          </button>
        </div>
      )}

      {/* Account Info */}
      <div className="text-center text-xs text-gray-400 pt-4">
        <p>Account created: {user?.metadata.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : 'Unknown'}</p>
        <p className="mt-1">Last sign in: {user?.metadata.lastSignInTime ? new Date(user.metadata.lastSignInTime).toLocaleDateString() : 'Unknown'}</p>
      </div>
    </div>
  );
};

export default ProfileSection;
